import { useTranslation } from 'react-i18next';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import Typography from '@mui/material/Typography';
import {
  AuditHistoryFormData,
  loadMoreAuditHistoryThunk,
} from '../../../state/auditHistoryModalSlice';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { formatAction, getAuditActionIcon } from './helpers';
import { useDispatch, useSelector } from '../../../state';
import { useEffect, useRef } from 'react';

export default function TimelineTab({
  formData,
}: {
  formData: AuditHistoryFormData;
}): JSX.Element {
  const dispatch = useDispatch();
  const { t: tMA } = useTranslation([], {
    keyPrefix: 'team.Modals.AuditHistory',
  });

  const { tableLoading } = useSelector(
    ({ auditHistoryModalSlice }) => auditHistoryModalSlice,
  );
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const items = formData?.results ?? [];

  useEffect(() => {
    if (!bottomRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          dispatch(loadMoreAuditHistoryThunk());
        }
      },
      {
        // start loading a bit before the bottom is fully visible
        root: null,
        rootMargin: '300px',
        threshold: 0,
      },
    );

    observer.observe(bottomRef.current);

    return () => observer.disconnect();
  }, [dispatch]);

  if (items.length === 0) {
    return (
      <Box
        sx={{
          py: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2">{tMA('No Results')}</Typography>
      </Box>
    );
  }
  return (
    <Timeline position="alternate">
      {items.map((log, idx) => {
        const isLast = idx === items.length - 1;
        const createdAt = dayjs(log.createdAt).format('MM/DD/YYYY h:mm A');
        return (
          <TimelineItem key={log.eventId}>
            <TimelineOppositeContent sx={{ m: 'auto 0' }}>
              <Typography variant="body2" color="text.secondary">
                {/* {log.createdAt} */}
                {createdAt}
              </Typography>
            </TimelineOppositeContent>

            <TimelineSeparator>
              {idx !== 0 && <TimelineConnector />}

              <Tooltip
                title={
                  <>
                    <div>
                      {tMA('Action')}: {String(log.action)}
                    </div>
                    <div>
                      {tMA('Actor')}: {String(log.actorType)} / {log.actorId}
                    </div>
                  </>
                }
              >
                {getAuditActionIcon(log.action)}
              </Tooltip>

              {!isLast && <TimelineConnector />}
            </TimelineSeparator>

            <TimelineContent sx={{ py: '12px', px: 2 }}>
              <Typography variant="h6" component="span">
                {formatAction(log.action)}
              </Typography>

              <Typography variant="body2">{log.detail}</Typography>

              {log.moreDetail ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {log.moreDetail}
                </Typography>
              ) : null}
            </TimelineContent>
          </TimelineItem>
        );
      })}
      <div ref={bottomRef} />
      {tableLoading === true ? (
        <Box
          height="50px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={40} />
        </Box>
      ) : null}
    </Timeline>
  );
}
